// DEM-kilde: EKTE høydedata fra Terrarium-terrengfliser (Mapzen/AWS «Terrain
// Tiles») → `ElevationGrid` som contracts-algoritmene (`heightProfile`,
// `slopeAspect`, `viewshed`) kan brukes rett på. Samme flis-kilde som COP-kartet
// allerede bruker til relieff (`apps/cop/src/map/basemaps.ts` → `addHillshade`),
// så vi drar ikke inn et nytt datasett-avhengighetsforhold.
//
// Filen er DELT I TO med vilje:
//
//   1. REN MATEMATIKK (alt fram til seksjonen «NETTLESER-DEL») — Terrarium-
//      dekoding, Web-Mercator flismatematikk og resampling. Ingen I/O, ingen
//      DOM. Fullt node-testbart (se dem.test.ts).
//   2. NETTLESER-DEL (nederst, tydelig merket) — henting av PNG-fliser til RGBA
//      via fetch + createImageBitmap + OffscreenCanvas. Denne KAN IKKE
//      node-testes (node har ingen PNG-dekoder og ingen OffscreenCanvas), og er
//      derfor holdt så tynn som mulig: den gjør ingenting annet enn å skaffe
//      RGBA-byte og sende dem videre til de rene funksjonene over.
//
// VIKTIG OM DATAKVALITET (les før du stoler på tallene operativt):
//   * Terrarium er et DTM (bakketerreng) — IKKE et DSM. Bygninger, skog og
//     master er IKKE med. En siktlinje som er «fri» over en granskog eller
//     gjennom en bydel kan være reelt blokkert. Legg på vegetasjons-/bygnings-
//     margin manuelt via `targetHeightM`/`requiredClearanceM` i los.ts.
//   * Kildedata i Norge er i praksis SRTM/EU-DEM-klasse, ~30 m horisontal
//     oppløsning og typisk noen meters vertikalt avvik. Maks flis-zoom er 14;
//     piksel-avstanden ved z=14 er ~4,8 m ved 60°N, men informasjonsinnholdet
//     er fortsatt ~30 m — høyere zoom gir INTERPOLERT detalj, ikke ny detalj.
//   * Høyder er ortometriske (over geoide/havnivå), ikke ellipsoide.
// ── Konstanter ───────────────────────────────────────────────────────────────
/** Flis-URL COP-kartet bruker (Mapzen/AWS Terrain Tiles, terrarium-koding). */
export const TERRARIUM_URL_TEMPLATE = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';
/** Terrarium-fliser er 256×256 piksler. */
export const TERRARIUM_TILE_SIZE = 256;
/** Datasettet finnes ikke over z=14 (høyere zoom = ren interpolasjon). */
export const TERRARIUM_MAX_ZOOM = 14;
/** Hull-verdi i `ElevationGrid.data` (samme som SRTM/DTED-leserne i contracts). */
export const VOID_HEIGHT = -32768;
/** Ytterste breddegrad Web-Mercator kan representere. */
export const MAX_MERCATOR_LAT = 85.05112877980659;
/** Mercator-sfærens radius (EPSG:3857 bruker WGS84-ekvatorradius). */
const MERCATOR_RADIUS_M = 6_378_137;
/** Meter per breddegrad — samme konstant som srtm.ts bruker for `cellSizeM`. */
const M_PER_DEG_LAT = 111_320;
const DEG = Math.PI / 180;
// ── Terrarium-dekoding ───────────────────────────────────────────────────────
/**
 * Dekoder Terrarium-RGBA til høyder i meter.
 *
 * Terrarium-koding (Mapzen):  h = (R * 256 + G + B / 256) - 32768
 *
 * R er «grov» meter-del, G er fin meter-del og B gir 1/256 m oppløsning.
 * Offset 32768 gjør at høyder under havnivå kan kodes (R=128,G=0,B=0 = 0 m).
 * Alfa-kanalen ignoreres (alltid 255 i disse flisene).
 *
 * REN funksjon: tar rå RGBA-byte, så den er testbar i node uten PNG-dekoder.
 * Returnerer `Float32Array` (ikke Int16) fordi B-kanalen gir sub-meter presisjon
 * som vi vil beholde gjennom resamplingen — avrunding skjer først i
 * `mosaicToGrid`.
 */
export function decodeTerrarium(rgba, width, height) {
    if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) {
        throw new Error(`decodeTerrarium: ugyldig bilde-dimensjon ${width}×${height}`);
    }
    const n = width * height;
    if (rgba.length < n * 4) {
        throw new Error(`decodeTerrarium: forventet ${n * 4} byte RGBA, fikk ${rgba.length}`);
    }
    const out = new Float32Array(n);
    for (let i = 0; i < n; i++) {
        const o = i * 4;
        out[i] = rgba[o] * 256 + rgba[o + 1] + rgba[o + 2] / 256 - 32768;
    }
    return out;
}
/** Klem breddegrad til Mercator-gyldig område (polene går mot uendelig). */
export function clampMercatorLat(lat) {
    return Math.min(MAX_MERCATOR_LAT, Math.max(-MAX_MERCATOR_LAT, lat));
}
/**
 * Global piksel-x for lengdegrad. «Global piksel» = piksel-koordinat i hele
 * verdenskartet på zoom z, dvs. tileSize · 2^z piksler bredt. Verdien er en
 * KANT-koordinat: piksel med indeks i dekker [i, i+1).
 */
export function lonToMercatorPixelX(lon, z, tileSize = TERRARIUM_TILE_SIZE) {
    return ((lon + 180) / 360) * tileSize * Math.pow(2, z);
}
/**
 * Global piksel-y for breddegrad (Mercator, y vokser sørover).
 *
 *   y_norm = 0.5 - ln(tan(π/4 + φ/2)) / (2π)
 *
 * Dette er den ikke-lineære biten: LIKE store piksel-steg i y gir ULIKE
 * breddegrad-steg. Derfor kan en Mercator-flis aldri brukes direkte som
 * `ElevationGrid` (som krever konstant `latStep`) — se `mosaicToGrid`.
 */
export function latToMercatorPixelY(lat, z, tileSize = TERRARIUM_TILE_SIZE) {
    const phi = clampMercatorLat(lat) * DEG;
    const yNorm = 0.5 - Math.log(Math.tan(Math.PI / 4 + phi / 2)) / (2 * Math.PI);
    return yNorm * tileSize * Math.pow(2, z);
}
/** Invers av `lonToMercatorPixelX`. */
export function mercatorPixelXToLon(px, z, tileSize = TERRARIUM_TILE_SIZE) {
    return (px / (tileSize * Math.pow(2, z))) * 360 - 180;
}
/** Invers av `latToMercatorPixelY`:  φ = atan(sinh(π · (1 - 2·y_norm))). */
export function mercatorPixelYToLat(py, z, tileSize = TERRARIUM_TILE_SIZE) {
    const yNorm = py / (tileSize * Math.pow(2, z));
    return Math.atan(Math.sinh(Math.PI * (1 - 2 * yNorm))) / DEG;
}
/** Fraksjonell flis-koordinat for (lon,lat) på zoom z. */
export function lonLatToTileFraction(lon, lat, z) {
    return {
        x: lonToMercatorPixelX(lon, z, 1),
        y: latToMercatorPixelY(lat, z, 1),
    };
}
/** Flisen som inneholder (lon,lat). Klemmes til gyldig [0, 2^z-1]. */
export function lonLatToTile(lon, lat, z) {
    const n = Math.pow(2, z);
    const f = lonLatToTileFraction(lon, lat, z);
    const clamp = (v) => Math.min(n - 1, Math.max(0, Math.floor(v)));
    return { z, x: clamp(f.x), y: clamp(f.y) };
}
/** Flisens geografiske grenser. */
export function tileBounds(t) {
    const n = Math.pow(2, t.z);
    return {
        west: (t.x / n) * 360 - 180,
        east: ((t.x + 1) / n) * 360 - 180,
        north: mercatorPixelYToLat(t.y, t.z, 1),
        south: mercatorPixelYToLat(t.y + 1, t.z, 1),
    };
}
/** Bakke-oppløsning (m per piksel) i Mercator ved gitt breddegrad og zoom. */
export function groundResolutionM(lat, z, tileSize = TERRARIUM_TILE_SIZE) {
    return ((2 * Math.PI * MERCATOR_RADIUS_M * Math.cos(clampMercatorLat(lat) * DEG)) /
        (tileSize * Math.pow(2, z)));
}
/**
 * Laveste zoom som gir minst ønsket oppløsning (m/piksel) ved breddegraden.
 * Kappes på `maxZoom` — over z=14 finnes ingen NY informasjon i Terrarium.
 */
export function zoomForResolution(lat, targetMetersPerPixel, tileSize = TERRARIUM_TILE_SIZE, maxZoom = TERRARIUM_MAX_ZOOM) {
    if (!(targetMetersPerPixel > 0))
        throw new Error('zoomForResolution: mål-oppløsning må være > 0');
    for (let z = 0; z < maxZoom; z++) {
        if (groundResolutionM(lat, z, tileSize) <= targetMetersPerPixel)
            return z;
    }
    return maxZoom;
}
/** Alle fliser som trengs for å dekke boksen på zoom z (radvis, nord→sør). */
export function tilesForBBox(bbox, z, tileSize = TERRARIUM_TILE_SIZE) {
    // Ta med én piksel margin: bilineær sampling nær kanten trenger nabo-pikselen,
    // som kan ligge i nabo-flisen.
    const n = Math.pow(2, z);
    const world = tileSize * n;
    const pxW = lonToMercatorPixelX(bbox.west, z, tileSize) - 1;
    const pxE = lonToMercatorPixelX(bbox.east, z, tileSize) + 1;
    const pyN = latToMercatorPixelY(bbox.north, z, tileSize) - 1;
    const pyS = latToMercatorPixelY(bbox.south, z, tileSize) + 1;
    const clampT = (v) => Math.min(n - 1, Math.max(0, v));
    const x0 = clampT(Math.floor(Math.max(0, pxW) / tileSize));
    const x1 = clampT(Math.floor(Math.min(world - 1, pxE) / tileSize));
    const y0 = clampT(Math.floor(Math.max(0, pyN) / tileSize));
    const y1 = clampT(Math.floor(Math.min(world - 1, pyS) / tileSize));
    const out = [];
    for (let y = y0; y <= y1; y++)
        for (let x = x0; x <= x1; x++)
            out.push({ z, x, y });
    return out;
}
/** Fyll inn {z}/{x}/{y} i en flis-URL-mal. */
export function tileUrl(template, t) {
    return template
        .replace('{z}', String(t.z))
        .replace('{x}', String(t.x))
        .replace('{y}', String(t.y));
}
/**
 * Boks rundt et punkt med gitt radius i meter (til sensor-/OP-dekning).
 * Lengdegrad-utvidelsen skaleres med cos(lat) slik at boksen faktisk dekker
 * radiusen i øst-vest også.
 */
export function bboxAround(center, radiusM) {
    const dLat = radiusM / M_PER_DEG_LAT;
    const cos = Math.max(0.01, Math.cos(center.lat * DEG));
    const dLon = radiusM / (M_PER_DEG_LAT * cos);
    return {
        north: Math.min(MAX_MERCATOR_LAT, center.lat + dLat),
        south: Math.max(-MAX_MERCATOR_LAT, center.lat - dLat),
        west: center.lon - dLon,
        east: center.lon + dLon,
    };
}
export function tileKey(x, y) {
    return `${x}/${y}`;
}
export function createMosaic(z, tileSize = TERRARIUM_TILE_SIZE) {
    return { z, tileSize, tiles: new Map() };
}
/** Legg en dekodet flis inn i mosaikken (validerer størrelsen). */
export function putTile(m, x, y, heights) {
    const want = m.tileSize * m.tileSize;
    if (heights.length !== want) {
        throw new Error(`putTile: flis ${x}/${y} har ${heights.length} verdier, forventet ${want}`);
    }
    m.tiles.set(tileKey(x, y), heights);
}
/**
 * Høyde i EN bestemt global piksel (heltallsindeks). null om flisen ikke er
 * lastet, om y er utenfor verden, eller om verdien ikke er et tall.
 * x wrappes rundt datolinja.
 */
export function mosaicPixelHeight(m, px, py) {
    const world = m.tileSize * Math.pow(2, m.z);
    const iy = Math.floor(py);
    if (iy < 0 || iy >= world)
        return null;
    let ix = Math.floor(px) % world;
    if (ix < 0)
        ix += world;
    const tx = Math.floor(ix / m.tileSize);
    const ty = Math.floor(iy / m.tileSize);
    const tile = m.tiles.get(tileKey(tx, ty));
    if (!tile)
        return null;
    const v = tile[(iy - ty * m.tileSize) * m.tileSize + (ix - tx * m.tileSize)];
    return Number.isFinite(v) ? v : null;
}
/**
 * Bilineær høyde ved FRAKSJONELL global piksel-koordinat.
 *
 * Terrarium-fliser er «pixel-is-area»: piksel i dekker [i, i+1) og har SENTER i
 * i+0.5. Interpolasjonen skjer derfor mellom pikselSENTRE — derav -0.5. Uten
 * dette får man et halvt piksels systematisk forskyvning (~2,4 m ved z=14).
 *
 * Returnerer null hvis noen av de fire nabopikslene mangler (uladet flis), slik
 * at hull forplantes ærlig i stedet for å bli gjettet, eller hvis py er utenfor
 * verden.
 *
 * I den ytterste HALVE pikselraden av verden (ved ±85°) finnes ingen nabo å
 * interpolere mot; da klemmes rad-indeksen til kantraden (samme grep som
 * contracts' `elevationAt` gjør på rutenett-kanten). Ingen oppdiktet data, bare
 * fravær av ekstrapolasjon. x wrappes rundt datolinja og trenger ingen klemming.
 */
export function mosaicHeightAtPixel(m, px, py) {
    const world = m.tileSize * Math.pow(2, m.z);
    if (py < -0.5 || py > world + 0.5)
        return null;
    const sx = px - 0.5;
    const sy = py - 0.5;
    const x0 = Math.floor(sx);
    const y0raw = Math.floor(sy);
    const fx = sx - x0;
    const fy = sy - y0raw;
    const clampRow = (r) => Math.min(world - 1, Math.max(0, r));
    const y0 = clampRow(y0raw);
    const y1 = clampRow(y0raw + 1);
    const h00 = mosaicPixelHeight(m, x0, y0);
    const h10 = mosaicPixelHeight(m, x0 + 1, y0);
    const h01 = mosaicPixelHeight(m, x0, y1);
    const h11 = mosaicPixelHeight(m, x0 + 1, y1);
    if (h00 === null || h10 === null || h01 === null || h11 === null)
        return null;
    const top = h00 * (1 - fx) + h10 * fx;
    const bot = h01 * (1 - fx) + h11 * fx;
    return top * (1 - fy) + bot * fy;
}
/** Bilineær høyde ved (lat,lon) direkte fra mosaikken. */
export function mosaicHeightAt(m, lat, lon) {
    return mosaicHeightAtPixel(m, lonToMercatorPixelX(lon, m.z, m.tileSize), latToMercatorPixelY(lat, m.z, m.tileSize));
}
/**
 * Rutenett-dimensjoner for en boks slik at cellene blir ~KVADRATISKE PÅ BAKKEN.
 *
 * Dette er viktig: `ElevationGrid.cellSizeM` er ÉN skalar som `slopeAspect`
 * bruker i begge akser. Ved 60°N er ett lengdegrad-steg bare halvparten så
 * langt på bakken som samme breddegrad-steg, så uten denne skaleringen blir
 * helning i øst-vest dobbelt så bratt som virkeligheten.
 */
export function gridDimsForBBox(bbox, targetMetersPerCell) {
    if (!(targetMetersPerCell > 0))
        throw new Error('gridDimsForBBox: cellestørrelse må være > 0');
    const midLat = (bbox.north + bbox.south) / 2;
    const heightM = (bbox.north - bbox.south) * M_PER_DEG_LAT;
    const widthM = (bbox.east - bbox.west) * M_PER_DEG_LAT * Math.cos(midLat * DEG);
    return {
        rows: Math.max(2, Math.round(heightM / targetMetersPerCell) + 1),
        cols: Math.max(2, Math.round(widthM / targetMetersPerCell) + 1),
    };
}
/**
 * Syr sammen mosaikken til ÉN ekvirektangulær `ElevationGrid` for en vilkårlig
 * boks og ønsket oppløsning — slik at `heightProfile`/`slopeAspect`/`viewshed`
 * fra contracts kan brukes rett på resultatet.
 *
 * DETTE ER RESAMPLINGEN, og den er hele poenget med funksjonen:
 * en Mercator-flis har IKKE konstant breddegrad-steg per pikselrad (steget
 * krymper med cos·sec-faktoren mot polene), men `ElevationGrid` KREVER konstant
 * `latStep`. Vi kan derfor ikke kopiere pikselrader rett over. For hver MÅL-rad
 * regner vi ut radens breddegrad, INVERTERER Mercator (`latToMercatorPixelY`) til
 * en fraksjonell piksel-y, og bilineær-sampler der. Feilen ved å anta jevnt
 * fordelte rader er flere grader over store bokser (se dem.test.ts).
 *
 * Mercator er separabel (x avhenger bare av lon, y bare av lat), så piksel-
 * koordinatene forhåndsberegnes per kolonne/rad — O(rows+cols) trigonometri.
 *
 * Geo-referansen følger contracts-konvensjonen: celle [0,0] er NORDVEST og
 * ligger EKSAKT på (north, west); rad rows-1 ligger på `south` (node-basert,
 * som srtm.ts/dted.ts og `ElevationService.covers`).
 */
export function mosaicToGrid(m, bbox, rows, cols) {
    if (!Number.isInteger(rows) || !Number.isInteger(cols) || rows < 2 || cols < 2) {
        throw new Error(`mosaicToGrid: trenger minst 2×2 celler, fikk ${rows}×${cols}`);
    }
    if (!(bbox.north > bbox.south) || !(bbox.east > bbox.west)) {
        throw new Error('mosaicToGrid: ugyldig boks (krever north>south og east>west)');
    }
    const latStep = (bbox.north - bbox.south) / (rows - 1);
    const lonStep = (bbox.east - bbox.west) / (cols - 1);
    const pxs = new Float64Array(cols);
    for (let c = 0; c < cols; c++) {
        pxs[c] = lonToMercatorPixelX(bbox.west + c * lonStep, m.z, m.tileSize);
    }
    const pys = new Float64Array(rows);
    for (let r = 0; r < rows; r++) {
        // Inverter Mercator per mål-rad — IKKE lineær pikselrad-avstand.
        pys[r] = latToMercatorPixelY(bbox.north - r * latStep, m.z, m.tileSize);
    }
    const data = new Int16Array(rows * cols);
    for (let r = 0; r < rows; r++) {
        const py = pys[r];
        const base = r * cols;
        for (let c = 0; c < cols; c++) {
            const h = mosaicHeightAtPixel(m, pxs[c], py);
            // Klem til Int16 MEN aldri til VOID-verdien, så en ekte høyde ikke
            // forveksles med hull.
            data[base + c] = h === null ? VOID_HEIGHT : Math.min(32767, Math.max(-32767, Math.round(h)));
        }
    }
    return {
        rows,
        cols,
        north: bbox.north,
        west: bbox.west,
        latStep,
        lonStep,
        // Nord-sør-steget er breddegrad-uavhengig; bruk `gridDimsForBBox` for at
        // øst-vest-steget skal bli like langt på bakken (se doc der).
        cellSizeM: Math.max(1, latStep * M_PER_DEG_LAT),
        data,
    };
}
/** Andel gyldige celler i et rutenett (dekningsgrad, 0–1) — til QA av lasting. */
export function gridCoverage(g) {
    let ok = 0;
    const n = g.rows * g.cols;
    for (let i = 0; i < n; i++)
        if (g.data[i] !== VOID_HEIGHT)
            ok++;
    return n === 0 ? 0 : ok / n;
}
/** [NETTLESER] Hent én flis og dekod til høyder. null = ingen flis (404 = ingen dekning). */
export async function fetchTerrariumTile(t, opts = {}) {
    const tileSize = opts.tileSize ?? TERRARIUM_TILE_SIZE;
    const doFetch = opts.fetchImpl ?? fetch;
    const res = await doFetch(tileUrl(opts.urlTemplate ?? TERRARIUM_URL_TEMPLATE, t), {
        signal: opts.signal,
    });
    if (res.status === 404)
        return null; // utenfor datasettet — ærlig hull, ikke 0 m
    if (!res.ok)
        throw new Error(`Terrarium-flis ${t.z}/${t.x}/${t.y}: HTTP ${res.status}`);
    const bitmap = await createImageBitmap(await res.blob());
    try {
        if (bitmap.width !== tileSize || bitmap.height !== tileSize) {
            throw new Error(`Terrarium-flis ${t.z}/${t.x}/${t.y} er ${bitmap.width}×${bitmap.height}, forventet ${tileSize}²`);
        }
        const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx)
            throw new Error('OffscreenCanvas: fikk ikke 2d-kontekst');
        ctx.drawImage(bitmap, 0, 0);
        const rgba = ctx.getImageData(0, 0, bitmap.width, bitmap.height).data;
        return decodeTerrarium(rgba, bitmap.width, bitmap.height);
    }
    finally {
        bitmap.close();
    }
}
/**
 * [NETTLESER] Enkel LRU-cache for dekodede fliser, med de-duplisering av
 * samtidige forespørsler på samme flis. Én flis ≈ 256 KB som Float32Array.
 */
export class TerrariumTileCache {
    cache = new Map();
    pending = new Map();
    maxTiles;
    opts;
    constructor(opts = {}) {
        this.maxTiles = opts.maxTiles ?? 256;
        this.opts = opts;
    }
    get size() {
        return this.cache.size;
    }
    clear() {
        this.cache.clear();
    }
    /** Hent flis (fra cache eller nett). null = ingen dekning. */
    async tile(t) {
        const key = `${t.z}/${t.x}/${t.y}`;
        if (this.cache.has(key)) {
            const hit = this.cache.get(key) ?? null;
            this.cache.delete(key); // flytt til «nyest brukt»
            this.cache.set(key, hit);
            return hit;
        }
        const inflight = this.pending.get(key);
        if (inflight)
            return inflight;
        const p = fetchTerrariumTile(t, this.opts)
            .then((h) => {
            this.cache.set(key, h);
            while (this.cache.size > this.maxTiles) {
                const oldest = this.cache.keys().next();
                if (oldest.done)
                    break;
                this.cache.delete(oldest.value);
            }
            return h;
        })
            .finally(() => this.pending.delete(key));
        this.pending.set(key, p);
        return p;
    }
}
/** [NETTLESER] Last alle fliser som dekker boksen på zoom z til en mosaikk. */
export async function loadMosaic(cache, bbox, z, tileSize = TERRARIUM_TILE_SIZE) {
    const mosaic = createMosaic(z, tileSize);
    const need = tilesForBBox(bbox, z, tileSize);
    // allSettled, IKKE all: én flis som feiler (HTTP 500, tidsavbrudd) skal gi
    // DELVIS dekning, ikke rive hele lastingen. Kall-stedene håndterer hull
    // allerede — `mosaicHeightAt` returnerer null utenfor lastede fliser, og
    // `gridCoverage` sier hvor stor andel som faktisk har data. Med `all` ville
    // ett dårlig flis-svar gitt tomt kartlag i stedet for det terrenget vi HAR.
    const results = await Promise.allSettled(need.map((t) => cache.tile(t)));
    need.forEach((t, i) => {
        const r = results[i];
        if (r.status === 'fulfilled' && r.value)
            putTile(mosaic, t.x, t.y, r.value);
    });
    return mosaic;
}
/**
 * [NETTLESER] Full kjede: boks → fliser → mosaikk → `ElevationGrid`.
 * Resultatet mates rett inn i los.ts/rf.ts og contracts-algoritmene.
 */
export async function loadElevationGrid(bbox, opts = {}) {
    const tileSize = opts.tileSize ?? TERRARIUM_TILE_SIZE;
    const cell = opts.targetMetersPerCell ?? 30;
    const midLat = (bbox.north + bbox.south) / 2;
    const z = opts.zoom ?? zoomForResolution(midLat, cell, tileSize);
    const cache = opts.cache ?? new TerrariumTileCache(opts);
    const mosaic = await loadMosaic(cache, bbox, z, tileSize);
    const { rows, cols } = gridDimsForBBox(bbox, cell);
    return mosaicToGrid(mosaic, bbox, rows, cols);
}
//# sourceMappingURL=dem.js.map